import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { getTheme } from "@/lib/theme";

// Landing route for the Emergent-managed Google Auth redirect.
// The user arrives at /auth/callback#session_id=... — we exchange it for the
// app's JWT, then route into the app. Processed once via a ref guard.
export default function AuthCallback() {
  const { applyAuth, selectedBusinessId } = useAuth();
  const nav = useNavigate();
  const processed = useRef(false);
  const [error, setError] = useState(false);
  const theme = getTheme(selectedBusinessId);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const search = window.location.search || "";
    const fromHash = new URLSearchParams(hash.replace(/^#/, ""));
    const fromQuery = new URLSearchParams(search);
    const sessionId = fromHash.get("session_id") || fromQuery.get("session_id");

    if (!sessionId) {
      setError(true);
      return;
    }

    (async () => {
      try {
        const businessId = localStorage.getItem("bj_selected_business") || undefined;
        const res = await api.googleSession(sessionId, businessId);
        applyAuth(res.access_token, res.user);
        // Clean the URL so the session_id isn't left in history
        window.history.replaceState(null, "", window.location.pathname);
        toast.success(`Welcome, ${res.user.name}!`);
        nav(res.user.role === "baker" ? "/manage" : "/shop", { replace: true });
      } catch (e) {
        toast.error(e.message || "Google sign-in failed");
        setError(true);
      }
    })();
  }, [applyAuth, nav, selectedBusinessId]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ background: "var(--bg)" }}>
      {error ? (
        <div className="text-center">
          <h1 className="font-display text-2xl" style={{ color: "var(--ink)" }}>Sign-in didn&rsquo;t complete</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>Please try again.</p>
          <button onClick={() => nav("/sign-in", { replace: true })} className="btn-brand mt-5 px-6 py-2.5 rounded-full font-semibold">
            Back to sign in
          </button>
        </div>
      ) : (
        <div className="text-center">
          <div
            className="mx-auto h-12 w-12 rounded-full border-2 animate-spin"
            style={{ borderColor: "var(--border)", borderTopColor: "var(--brand)" }}
          />
          <p className="mt-5 font-display text-xl" style={{ color: "var(--ink)" }}>Signing you in…</p>
          <p className="text-sm" style={{ color: "var(--muted)" }}>{theme.name}</p>
        </div>
      )}
    </div>
  );
}
